import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Globe, Search, Home, Building2, Briefcase, Users, Phone, Info } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useOptimizedNavigationMenu } from '@/hooks/useOptimizedNavigationMenu';
import { useSettings, useMediaLibrary } from '@/hooks/useSupabaseData';

type IconType = React.ComponentType<{ size?: number | string; className?: string }>;
const ICON_MAP: Record<string, IconType> = {
  '/': Home,
  '/sectors': Building2,
  '/companies': Briefcase,
  '/projects': Briefcase,
  '/about': Info,
  '/contact': Phone,
  '/partners': Users,
};

interface EnhancedMobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  variant?: 'home' | 'internal' | 'sticky';
}

const EnhancedMobileSidebar: React.FC<EnhancedMobileSidebarProps> = ({
  isOpen,
  onClose,
  variant = 'internal'
}) => {
  const { language, setLanguage } = useLanguage();
  const location = useLocation();

  // Fetch navigation and settings
  const { data: navigationMenu, isLoading } = useOptimizedNavigationMenu('main-header');
  const { getSetting } = useSettings();
  const { media: logos } = useMediaLibrary('logo');

  // Get logos and site info
  const darkLogo = logos?.find(logo => logo.title_en === 'Logo Dark');
  const siteTitle = getSetting('site_title');

  type NavEntry = { label: string; href: string; icon: IconType };
  const navigationItems: NavEntry[] = React.useMemo(() => {
    if (!navigationMenu?.items) return [];
    
    return navigationMenu.items.map(item => {
      const href = item.external_url || (item.slug ? `/${String(item.slug).replace(/^\/+/, '')}` : '#');
    const IconComponent = ICON_MAP[href] || Building2;
      
      return {
        label: language === 'ar' ? item.title_ar : item.title_en,
        href,
        icon: IconComponent
      };
    });
  }, [navigationMenu?.items, language]);

  const isActivePath = (path: string): boolean => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Animation variants
  const sidebarVariants = {
    closed: {
      x: language === 'ar' ? '-100%' : '100%',
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 40
      }
    },
    open: {
      x: 0,
      transition: {
        type: "spring" as const,
        stiffness: 400,
        damping: 40
      }
    }
  };

  const itemVariants = {
    closed: { opacity: 0, x: language === 'ar' ? -20 : 20 },
    open: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
        ease: "easeOut" as const
      }
    })
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Sidebar Panel */}
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={sidebarVariants}
            className={`fixed top-0 z-[9999] h-full w-80 max-w-[85vw] bg-background/95 backdrop-blur-lg border shadow-2xl ${
              language === 'ar' 
                ? 'left-0 border-r border-border' 
                : 'right-0 border-l border-border'
            }`}
          >
            <div className="flex flex-col h-full overflow-hidden">
              
              {/* Header */}
              <motion.div 
                className="flex items-center justify-between p-6 border-b border-border bg-gradient-to-r from-primary/5 to-secondary/5"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  {darkLogo ? (
                    <img
                      src={darkLogo.file_url}
                      alt={language === 'ar' ? darkLogo.alt_ar : darkLogo.alt_en}
                      className="h-8 w-auto"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                      <span className="text-primary-foreground font-bold text-sm">
                        {siteTitle?.charAt(0) || 'A'}
                      </span>
                    </div>
                  )}
                  <div>
                    <h2 className={`text-lg font-semibold text-foreground ${
                      language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'
                    }`}>
                      {language === 'ar' ? 'القائمة' : 'Menu'}
                    </h2>
                  </div>
                </div>
                
                <motion.button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-accent transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-5 h-5 text-foreground" />
                </motion.button>
              </motion.div>

              {/* Search moved to footer for app-like UX */}

              {/* Navigation Items */}
              <nav className="flex-1 overflow-y-auto py-4">
                <div className="px-4 space-y-2">
                  {isLoading ? (
                    // Loading skeleton
                    [...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="flex items-center space-x-3 rtl:space-x-reverse p-3 rounded-lg"
                      >
                        <div className="w-5 h-5 bg-muted rounded animate-pulse" />
                        <div className="flex-1 h-4 bg-muted rounded animate-pulse" />
                      </div>
                    ))
                  ) : (
                    navigationItems.map((item, index) => {
                      const isActive = isActivePath(item.href);
                      const Icon = item.icon;
                      
                      return (
                        <motion.div
                          key={item.href}
                          custom={index}
                          variants={itemVariants}
                          initial="closed"
                          animate="open"
                        >
                          <Link
                            to={item.href}
                            onClick={onClose}
                            className={`relative flex items-center space-x-3 rtl:space-x-reverse p-3 rounded-lg text-sm font-medium transition-all duration-200 group ${
                              isActive
                                ? 'bg-primary text-primary-foreground shadow-md'
                                : 'text-foreground hover:bg-accent hover:text-primary'
                            }`}
                          >
                            <Icon 
                              size={20} 
                              className={`flex-shrink-0 transition-transform group-hover:scale-110 ${
                                isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-primary'
                              }`} 
                            />
                            <span className={`flex-1 ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>
                              {item.label}
                            </span>
                            
                            {/* Active indicator */}
                            {isActive && (
                              <motion.div
                                className={`absolute ${language === 'ar' ? 'left-0' : 'right-0'} top-0 bottom-0 w-1 bg-primary-foreground rounded-${language === 'ar' ? 'r' : 'l'}-full`}
                                layoutId="mobileActiveIndicator"
                                transition={{ duration: 0.3 }}
                              />
                            )}
                            
                            {/* Hover effect */}
                            <motion.div
                              className="absolute inset-0 bg-primary/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              whileHover={{ scale: 1.02 }}
                            />
                          </Link>
                        </motion.div>
                      );
                    })
                  )}
                </div>
              </nav>

              {/* Footer: Search + Controls */}
              <motion.div
                className="p-4 border-t border-border bg-gradient-to-r from-accent/30 to-secondary/10 space-y-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder={language === 'ar' ? 'البحث...' : 'Search...'}
                    className={`w-full pl-10 rtl:pl-4 rtl:pr-10 pr-4 py-3 bg-accent/30 border border-border rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                      language === 'ar' ? 'font-arabic-body text-right' : 'font-english-body text-left'
                    }`}
                  />
                </div>
                {/* Language Toggle */}
                <motion.button
                  onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
                  className="w-full flex items-center justify-center space-x-3 rtl:space-x-reverse px-4 py-3 rounded-lg border border-border/50 bg-background/50 text-foreground hover:bg-accent transition-all duration-200 group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Globe className="w-5 h-5 text-primary group-hover:rotate-12 transition-transform" />
                  <span className={`font-medium ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>
                    {language === 'ar' ? 'Switch to English' : 'تبديل للعربية'}
                  </span>
                </motion.button>

                {/* Version info */}
                <div className="mt-3 text-center">
                  <span className="text-xs text-muted-foreground">
                    v1.0.0 • {new Date().getFullYear()}
                  </span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EnhancedMobileSidebar;